import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { fromJS } from "immutable";
import { addDays, format, nextMonday, parse, set } from "date-fns";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { DayPicker } from "react-day-picker";
import { Menu, MenuButton, MenuItem, MenuItems, Switch } from "@headlessui/react";
import {
  Ban,
  Calendar,
  ChevronDown,
  Send,
  Trash2,
  Lock,
  Eye,
  EyeOff,
  LockOpen,
} from "lucide-react";
import ActivityAction from "../actions/ActivityAction";
import LayoutAction from "../actions/LayoutAction";
import { getAppLink, getCurrentAzureOwner, getCurrentGoogleOwner } from "../api/AppLinkApi";
import { Button, Modal, SelectField } from "../components/ui";
import { getBaseUrl } from "../api/api";
import ComposeEmail from "../activities/ComposeEmail";
import {
  cancelScheduledEmail,
  deleteDraftEmail,
  saveDraftEmail,
  scheduledDraftEmail,
  sendDraftEmail,
} from "../api/EmailApi";
import InfoDialog from "./InfoDialog";
import OctagonAlertIcon from "../icons/OctagonAlertIcon";
import { getItem } from "../api/ProjectApi";
import DialogLayout from "./DialogLayout";
import ConfirmDialog from "./ConfirmDialog";
import TriangleAlertIcon from "../icons/TriangleAlertIcon";

const EmailDialog = ({ onSearchTasks }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { projectId }: any = useParams();

  const project = useSelector((state: any) => state.projectReducer.get("project"));
  const showEmail = useSelector((state: any) => state.activityReducer.get("showEmail"));

  const emailId = useSelector((state: any) => state.activityReducer.getIn(["email", "id"]));
  const itemId = useSelector((state: any) => state.activityReducer.getIn(["email", "itemId"]));
  const emailData = useSelector((state: any) => state.activityReducer.get("email")) || fromJS({});
  const tracking = emailData.get("tracking") || false;
  const visibility = emailData.get("visibility") || "Shared";

  const isEditMode = emailData.get("emailStatus") === "DRAFT";

  const [appGoogleData, setAppGoogleData] = useState<any>(fromJS({}));
  const [appAzureData, setAppAzureData] = useState<any>(fromJS({}));

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [showConfirm, setShowConfirm] = useState<any>({
    show: false,
    title: "",
    description: "",
  });

  const [showScheduleSend, setShowScheduleSend] = useState(false);
  const [isPickDateTime, setIsPickDateTime] = useState(false);

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [hour, setHour] = useState(parseInt(format(new Date(), "h")));
  const [minute, setMinute] = useState(parseInt(format(new Date(), "mm")));
  const [ampm, setAmpm] = useState<"AM" | "PM">(format(new Date(), "a") as "AM" | "PM");

  const dateNow = new Date();
  const tomorrowMorning = set(addDays(dateNow, 1), {
    hours: 8,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });
  const tomorrowAfternoon = set(addDays(dateNow, 1), {
    hours: 13,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });
  const mondayMorning = set(nextMonday(dateNow), {
    hours: 8,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });

  const editorInputDescriptionRef = useRef(null);

  const hasGmail = appGoogleData.get("connected") || false;
  const googleTokenExpired = appGoogleData.get("tokenExpired") || false;
  const hasOutlook = appAzureData.get("connected") || false;

  useEffect(() => {
    if (showEmail && itemId) {
      getItem(itemId).then((data) => {
        const searchItem = fromJS(data);
        dispatch(
          ActivityAction.changeEmailData("item", {
            value: searchItem.get("id"),
            label: searchItem.getIn(["data", "name", "value"]),
            type: searchItem.get("type"),
          })
        );
      });
    }
  }, [showEmail, itemId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadGoogleData();
    loadAzureData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (showEmail) {
      if (hasOutlook) {
        dispatch(ActivityAction.changeEmailData("provider", "azure"));
      }
      if (hasGmail) {
        dispatch(ActivityAction.changeEmailData("provider", "google"));
      }
    }
  }, [showEmail, hasGmail, hasOutlook]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadGoogleData = () => {
    getAppLink("google")
      .then((data) => {
        if (data.connected) {
          getCurrentGoogleOwner()
            .then((dataUser) => {
              setAppGoogleData(
                fromJS({
                  app: "google",
                  name: dataUser.name,
                  email: dataUser.email,
                  ...data,
                })
              );
            })
            .catch((error) => {
              console.info("Failed to get Google owner:", error);
              // Token expired/invalid — keep connected:true (still in DB)
              // but flag tokenExpired so UI can show a warning
              setAppGoogleData(
                fromJS({
                  app: "google",
                  name: t("Anonymous"),
                  email: "",
                  tokenExpired: true,
                  ...data,
                })
              );
            });
        } else {
          setAppGoogleData(
            fromJS({
              app: "google",
              name: t("Anonymous"),
              email: "",
              ...data,
            })
          );
        }
      })
      .catch((error) => {
        console.info("Failed to get Google app link:", error);
        // Set default state when app link fetch fails
        setAppGoogleData(
          fromJS({
            app: "google",
            name: t("Anonymous"),
            email: "",
            connected: false,
          })
        );
      });
  };

  const loadAzureData = () => {
    getAppLink("azure")
      .then((data) => {
        if (data.connected) {
          getCurrentAzureOwner()
            .then((dataUser) => {
              setAppAzureData(
                fromJS({
                  app: "azure",
                  name: dataUser.displayName,
                  email: dataUser.mail,
                  ...data,
                })
              );
            })
            .catch((error) => {
              console.error("Failed to get Azure owner:", error);
              // Set state to disconnected when API fails
              setAppAzureData(
                fromJS({
                  app: "azure",
                  name: t("Anonymous"),
                  email: "",
                  connected: false,
                  ...data,
                })
              );
            });
        } else {
          setAppAzureData(
            fromJS({
              app: "azure",
              name: t("Anonymous"),
              email: "",
              ...data,
            })
          );
        }
      })
      .catch((error) => {
        console.error("Failed to get Azure app link:", error);
        // Set default state when app link fetch fails
        setAppAzureData(
          fromJS({
            app: "azure",
            name: t("Anonymous"),
            email: "",
            connected: false,
          })
        );
      });
  };

  const getTimezoneName = () => {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZoneName: "long",
    });
    const parts = formatter.formatToParts(new Date());
    return parts.find((part) => part.type === "timeZoneName")?.value;
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    const data = emailData.toJS();

    if (!data.subject?.trim()) {
      errors.subject = t("Subject is required");
    }
    if (!data.body?.trim()) {
      errors.body = t("Content is required");
    }
    if (!data.to || data.to.length === 0) {
      errors.to = t("Recipients are required");
    }
    if (data.item?.value == undefined || data.item?.value === "") {
      errors.linkItem = t("Please select an item to send email");
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const renderIconCreateOrUpdate = () => {
    if (hasGmail) {
      return (
        <img
          className="w-4 h-4 object-contain"
          src={`${getBaseUrl()}/static/images/google.png`}
          alt="Google"
        />
      );
    }
    if (hasOutlook) {
      return (
        <img
          className="w-4 h-4 object-contain"
          src={`${getBaseUrl()}/static/images/microsoft.png`}
          alt="Microsoft"
        />
      );
    }
    return null;
  };

  const handleOnChange = (fieldId, fieldValue) => {
    dispatch(ActivityAction.changeEmailData(fieldId, fieldValue));
  };

  const handleSubmitConfirm = () => {
    if (showConfirm.type === "DELETE") {
      deleteDraftEmail(projectId, emailId)
        .then(() => {
          onSearchTasks({ projectId });
          handleClose();
          dispatch(
            LayoutAction.setNotification({
              title: t("Delete draft email"),
              type: "success",
              message: t("Delete draft email successfully!"),
            })
          );
        })
        .catch(() => {
          dispatch(
            LayoutAction.setNotification({
              title: t("Delete draft email"),
              type: "error",
              message: t("Failed to delete draft email"),
            })
          );
        });
    } else if (showConfirm.type === "CANCEL_EMAIL") {
      cancelScheduledEmail(projectId, emailId)
        .then(() => {
          onSearchTasks({ projectId });
          handleClose();
          dispatch(
            LayoutAction.setNotification({
              title: t("Cancel this email"),
              type: "success",
              message: t("Cancel this email successfully!"),
            })
          );
        })
        .catch(() => {
          dispatch(
            LayoutAction.setNotification({
              title: t("Cancel this email"),
              type: "error",
              message: t("Failed to Cancel this email"),
            })
          );
        });
    }
    setShowConfirm({ show: false, title: "", description: "" });
  };

  const handleSaveDraft = () => {
    const data = {
      ...emailData.toJS(),
    };
    if (data.emailStatus !== "DRAFT") {
      return;
    }
    if (!data.subject?.trim()) {
      // Delete draft
      deleteDraftEmail(projectId, emailId).then(() => {
        onSearchTasks({ projectId });
      });
      return;
    }
    saveDraftEmail(projectId, data)
      .then(() => {
        onSearchTasks({ projectId });
        dispatch(
          LayoutAction.setNotification({
            title: "Email",
            type: "success",
            message: t("Saved draft"),
          })
        );
      })
      .catch(() => {
        dispatch(
          LayoutAction.setNotification({
            title: "Email",
            type: "error",
            message: t("Failed to save draft"),
          })
        );
      });
  };

  const handleSend = () => {
    if (!validateForm()) {
      return;
    }
    const data = {
      ...emailData.toJS(),
    };
    saveDraftEmail(projectId, data)
      .then(() => {
        sendDraftEmail(projectId, emailId)
          .then(() => {
            onSearchTasks({ projectId });
            handleClose();
            dispatch(
              LayoutAction.setNotification({
                title: "Email",
                type: "success",
                message: "The email has been sent",
              })
            );
          })
          .catch(() => {
            dispatch(
              LayoutAction.setNotification({
                title: "Email",
                type: "error",
                message: t("Failed to send email"),
              })
            );
          });
      })
      .catch(() => {
        dispatch(
          LayoutAction.setNotification({
            title: "Email",
            type: "error",
            message: t("Failed to save draft"),
          })
        );
      });
  };

  const handleScheduleSend = (scheduleSend: Date) => {
    if (!validateForm()) {
      return;
    }
    const data = {
      ...emailData.toJS(),
    };
    saveDraftEmail(projectId, data)
      .then(() => {
        scheduledDraftEmail(projectId, emailId, {
          sendAt: scheduleSend.toISOString(),
        })
          .then(() => {
            onSearchTasks({ projectId });
            handleClose();
            dispatch(
              LayoutAction.setNotification({
                title: "Email",
                type: "success",
                message: "Scheduled email",
              })
            );
          })
          .catch(() => {
            dispatch(
              LayoutAction.setNotification({
                title: "Email",
                type: "error",
                message: t("Failed to scheduled email"),
              })
            );
          });
      })
      .catch(() => {
        dispatch(
          LayoutAction.setNotification({
            title: "Email",
            type: "error",
            message: t("Failed to save draft"),
          })
        );
      });
  };

  const handleClose = () => {
    setValidationErrors({});
    dispatch(ActivityAction.hideEmailDialog());
  };

  const handleCloseScheduleSend = () => {
    setSelectedDate(new Date());
    setHour(parseInt(format(new Date(), "h")));
    setMinute(parseInt(format(new Date(), "mm")));
    setAmpm(format(new Date(), "a") as "AM" | "PM");
    setIsPickDateTime(false);
    setShowScheduleSend(false);
  };

  const renderScheduleSend = () => {
    if (isPickDateTime) {
      return (
        <div style={{ width: 500 }}>
          <div className="pt-2 flex relative items-center">
            <div className="pl-4 text-xl font-bold">{t("Pick date & time")}</div>
            <div className={"absolute right-2"}>
              <XMarkIcon
                onClick={handleCloseScheduleSend}
                className={"w-5 cursor-pointer hover:text-blue-600"}
              />
            </div>
          </div>
          <div className="p-4">
            <div className="p-4 flex justify-center">
              <DayPicker
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                className="w-auto"
                numberOfMonths={1}
                weekStartsOn={1}
                showOutsideDays
                fixedWeeks
              />
            </div>
            <div className="px-4 pb-4 border-t border-gray-200">
              <div className="pt-4 flex justify-center">
                <div className="flex items-center gap-2">
                  <div className="w-20">
                    <SelectField
                      value={{
                        value: hour,
                        label: String(hour).padStart(2, "0"),
                      }}
                      onChange={(option: any) => setHour(Number(option?.value))}
                      options={Array.from({ length: 12 }, (_, i) => ({
                        value: i + 1,
                        label: String(i + 1).padStart(2, "0"),
                      }))}
                      placeholder="09"
                      menuPortalTarget={null}
                      menuPosition="fixed"
                      isClearable={false}
                      isSearchable={false}
                    />
                  </div>
                  <span className="text-gray-500">:</span>
                  <div className="w-20">
                    <SelectField
                      value={{
                        value: minute,
                        label: String(minute).padStart(2, "0"),
                      }}
                      onChange={(option: any) => setMinute(Number(option?.value))}
                      options={Array.from({ length: 60 }, (_, i) => i).map((m) => ({
                        value: m,
                        label: String(m).padStart(2, "0"),
                      }))}
                      placeholder="30"
                      menuPortalTarget={null}
                      menuPosition="fixed"
                      isClearable={false}
                      isSearchable={false}
                    />
                  </div>
                  <div className="w-20">
                    <SelectField
                      value={{
                        value: ampm,
                        label: ampm,
                      }}
                      onChange={(option: any) => setAmpm(option?.value)}
                      options={[
                        { value: "AM", label: "AM" },
                        { value: "PM", label: "PM" },
                      ]}
                      placeholder="AM"
                      menuPortalTarget={null}
                      menuPosition="fixed"
                      isClearable={false}
                      isSearchable={false}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="px-4 pb-4 space-y-1">
              {validationErrors.dateRange && (
                <div className="text-xs text-red-500 text-center">{validationErrors.dateRange}</div>
              )}
            </div>
          </div>
          <div className="w-full">
            <div className={"flex space-x-2 justify-center w-full p-3"}>
              <button
                onClick={handleCloseScheduleSend}
                type="button"
                className="rounded-sm px-4 py-2 text-xs font-semibold shadow-xs border-1 border-gray-300 border-solid hover:bg-blue-500 hover:text-white focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-blue-600"
              >
                {t("Cancel")}
              </button>
              <button
                onClick={() => {
                  const parsedTime = parse(`${hour}:${minute} ${ampm}`, "h:mm a", new Date());
                  const mergedDate = set(selectedDate, {
                    hours: parsedTime.getHours(),
                    minutes: parsedTime.getMinutes(),
                    seconds: 0,
                    milliseconds: 0,
                  });
                  handleScheduleSend(mergedDate);
                  handleCloseScheduleSend();
                }}
                type="button"
                className="rounded-sm px-4 py-2 text-xs font-semibold shadow-xs text-white bg-blue-600 hover:bg-blue-500 focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {t("Schedule send")}
              </button>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div style={{ width: 400 }}>
        <div className="pt-2 flex relative items-center">
          <div className="pl-4 text-xl font-bold">{t("Schedule send")}</div>
          <div className={"absolute right-2"}>
            <XMarkIcon
              onClick={() => setShowScheduleSend(false)}
              className={"w-5 cursor-pointer hover:text-blue-600"}
            />
          </div>
        </div>
        <div className="pl-4 text-sm text-gray-600">{getTimezoneName()}</div>
        <div className="p-4 space-y-2">
          <div
            className="px-4 py-2 flex items-center justify-between rounded-md hover:bg-gray-200 cursor-pointer"
            onClick={() => {
              handleScheduleSend(tomorrowMorning);
              setShowScheduleSend(false);
            }}
          >
            <div className="text-sm text-gray-800">{t("Tomorrow morning")}</div>
            <div className="text-sm text-gray-600">{format(tomorrowMorning, "MMM dd, HH:mm")}</div>
          </div>
          <div
            className="px-4 py-2 flex items-center justify-between rounded-md hover:bg-gray-200 cursor-pointer"
            onClick={() => {
              handleScheduleSend(tomorrowAfternoon);
              setShowScheduleSend(false);
            }}
          >
            <div className="text-sm text-gray-800">{t("Tomorrow afternoon")}</div>
            <div className="text-sm text-gray-600">
              {format(tomorrowAfternoon, "MMM dd, HH:mm")}
            </div>
          </div>
          <div
            className="px-4 py-2 flex items-center justify-between rounded-md hover:bg-gray-200 cursor-pointer"
            onClick={() => {
              handleScheduleSend(mondayMorning);
              setShowScheduleSend(false);
            }}
          >
            <div className="text-sm text-gray-800">{t("Monday morning")}</div>
            <div className="text-sm text-gray-600">{format(mondayMorning, "MMM dd, HH:mm")}</div>
          </div>
          <div className="w-full my-2 border-t border-gray-200"></div>
          <div
            className="px-4 py-2 flex items-center gap-2 rounded-md hover:bg-gray-200 cursor-pointer"
            onClick={() => setIsPickDateTime(true)}
          >
            <Calendar className="w-4 h-4" />
            <span className="text-sm">{t("Pick date & time")}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderLeftFooter = () => {
    if (emailData.get("emailStatus") === "DRAFT") {
      return (
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Switch
              checked={visibility === "Shared"}
              onChange={(checked) => {
                if (checked) {
                  handleOnChange("visibility", "Shared");
                } else {
                  handleOnChange("visibility", "Private");
                }
              }}
              className="group relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-red-600 transition-colors duration-200 ease-in-out focus:outline-none data-[checked]:bg-green-600"
            >
              <Lock
                className={`absolute z-1 top-1 left-1 h-3 w-3 ${
                  visibility === "Shared" ? "text-white" : "text-red-600"
                }`}
              />
              <LockOpen
                className={`absolute z-1 top-1 right-1 h-3 w-3 ${
                  visibility === "Shared" ? "text-green-600" : "text-white"
                }`}
              />
              <span
                aria-hidden="true"
                className="pointer-events-none inline-block size-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
              />
            </Switch>
            <span className="text-gray-500 text-sm font-semibold">
              {visibility === "Shared" ? t("Shared") : t("Private")}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <Switch
              checked={tracking}
              onChange={(value) => handleOnChange("tracking", value)}
              className="group relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 data-[checked]:bg-blue-600"
            >
              <EyeOff
                className={`absolute z-1 top-1 left-1 h-3 w-3 ${
                  tracking ? "text-white" : "text-blue-600"
                }`}
              />
              <Eye
                className={`absolute z-1 top-1 right-1 h-3 w-3 ${
                  tracking ? "text-blue-600" : "text-gray-600"
                }`}
              />
              <span
                aria-hidden="true"
                className="pointer-events-none inline-block size-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
              />
            </Switch>
            <span className="text-gray-500 text-sm font-semibold">{t("Tracking")}</span>
          </div>
        </div>
      );
    }
    if (emailData.get("emailStatus") === "SCHEDULED" && !!emailData.get("sendAtUtc")) {
      return (
        <div className="text-sm text-red-600 font-medium">
          {`This email will be sent at ${format(new Date(emailData.get("sendAtUtc")), "hh:mm aa, yyyy-MM-dd")}`}
        </div>
      );
    }
    return null;
  };

  const renderRightFooter = () => {
    if (emailData.get("emailStatus") === "DRAFT") {
      return (
        <>
          <Button
            size="sm"
            variant="secondary"
            className="outline-none"
            onClick={() => {
              setShowConfirm({
                show: true,
                title: t("Delete draft email"),
                description: t("Are you sure you want to remove this draft email?"),
                alertIcon: <TriangleAlertIcon />,
                dangerBtn: true,
                type: "DELETE",
              });
            }}
          >
            <Trash2 className="w-4 h-4 text-red-600" />
          </Button>
          <Menu as="div" className="relative inline-flex z-20">
            <button
              className={`flex items-center gap-2 pl-3 rounded-lg bg-blue-600 text-sm text-white hover:bg-blue-700 border border-gray-200 disabled:bg-gray-50 disabled:text-gray-400 cursor-pointer`}
              onClick={handleSend}
            >
              {renderIconCreateOrUpdate()}
              {t("Send")}
              <div className="flex items-center">
                <span className="h-5 border-1 border-white/40"></span>
                <MenuButton
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  as="span"
                  className="inline-flex items-center pl-2 pr-3 py-2"
                >
                  <ChevronDown className="w-4 h-4" />
                </MenuButton>
              </div>
            </button>
            <MenuItems
              transition
              anchor="top end"
              className="z-50 rounded-lg border border-gray-200 bg-white p-1 transition duration-100 ease-out [--anchor-gap:0px] focus:outline-none data-closed:scale-95 data-closed:opacity-0"
            >
              <MenuItem>
                <button
                  onClick={() => setShowScheduleSend(true)}
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-100 whitespace-nowrap data-focus:bg-gray-200 cursor-pointer"
                >
                  <Send className="w-4 h-4 text-gray-800" />
                  {t("Schedule send")}
                </button>
              </MenuItem>
            </MenuItems>
          </Menu>
        </>
      );
    }
    if (emailData.get("emailStatus") === "SCHEDULED") {
      return (
        <Button
          size="sm"
          variant="primary"
          onClick={() => {
            setShowConfirm({
              show: true,
              title: t("Email"),
              description: t("Are you sure you want to cancel this email?"),
              alertIcon: <TriangleAlertIcon />,
              dangerBtn: true,
              type: "CANCEL_EMAIL",
            });
          }}
          leftIcon={<Ban className="w-4 h-4 text-white" />}
        >
          {t("Cancel this email")}
        </Button>
      );
    }
    return null;
  };

  const modalContent = (
    <div className="p-4">
      <ComposeEmail
        projectId={project.get("id")}
        isEditMode={isEditMode}
        emailData={emailData.toJS()}
        onDataChange={(fieldId, fieldValue) => {
          handleOnChange(fieldId, fieldValue);
        }}
        validationErrors={validationErrors}
        editorInputDescriptionRef={editorInputDescriptionRef}
        navigateToUrl={(url) => {
          navigate(url);
          handleSaveDraft();
          handleClose();
        }}
      />
      <DialogLayout show={showScheduleSend}>{renderScheduleSend()}</DialogLayout>
    </div>
  );

  const modalFooter = (
    <div className="flex items-center justify-between">
      <div className="flex items-center space-x-2">{renderLeftFooter()}</div>
      <div className="flex items-center justify-end gap-3">{renderRightFooter()}</div>
    </div>
  );

  if (!showEmail || !emailId) return null;

  if ((!hasGmail || googleTokenExpired) && !hasOutlook) {
    return (
      <InfoDialog
        visible={showEmail}
        width={500}
        alertIcon={<OctagonAlertIcon />}
        title={t("Connect to compose email")}
        description={
          <div className="space-y-4">
            <div>
              {t(
                "You need to connect with your Google/Microsoft account to sync email and calendar"
              )}
            </div>
            <div className="flex items-center justify-center gap-2">
              <Button
                size="sm"
                variant="primary"
                onClick={() => {
                  navigate(
                    `/workspaces/${projectId}/settings/apps?key=Apps&appId=google&item=Gmail`
                  );
                  handleClose();
                }}
                leftIcon={
                  <img
                    className="w-4 h-4 object-contain"
                    src={`${getBaseUrl()}/static/images/google.png`}
                    alt="Google"
                  />
                }
              >
                {t("Connect with Google")}
              </Button>
              <Button
                size="sm"
                variant="primary"
                onClick={() => {
                  navigate(
                    `/workspaces/${projectId}/settings/apps?key=Apps&appId=azure&item=Outlook`
                  );
                  handleClose();
                }}
                leftIcon={
                  <img
                    className="w-4 h-4 object-contain"
                    src={`${getBaseUrl()}/static/images/microsoft.png`}
                    alt="Microsoft"
                  />
                }
              >
                {t("Connect with Microsoft")}
              </Button>
            </div>
          </div>
        }
        onClose={handleClose}
      />
    );
  }

  return (
    <Modal
      isOpen={showEmail}
      onClose={() => {
        handleSaveDraft();
        handleClose();
      }}
      title={t("Compose email")}
      className="w-auto max-h-[98vh]"
      footer={modalFooter}
    >
      {modalContent}
      <ConfirmDialog
        visible={showConfirm.show}
        title={showConfirm.title}
        description={showConfirm.description}
        alertIcon={showConfirm.alertIcon || <OctagonAlertIcon />}
        dangerBtn={showConfirm.dangerBtn}
        onSubmit={handleSubmitConfirm}
        onClose={() => {
          setShowConfirm({ show: false, title: "", description: "" });
        }}
      />
    </Modal>
  );
};

export default EmailDialog;
